import { routeExample, viaX } from "./example-layout";
import { heroSystem } from "./hero-system";
import type { ComponentNode, System, TerminalSide } from "./types";

const terminal = (side: TerminalSide, offset: number) => ({ side, offset, pinned: true });
type Presentation = Partial<Pick<ComponentNode, "position" | "label" | "rotation" | "terminalOverrides">>;

/** A compact homepage circuit based on the square Meta creative's layout.
 * Solar and controller share the top row, above the battery and loads.
 * Equipment, cable lengths and protection remain the original hero's; only
 * presentation changes. The shared landscape example stays available to guides. */
export function heroPortraitSystem(): System {
  const original = heroSystem();
  const geometry: Record<string, Presentation> = {
    solar: { position: { x: 40, y: 0 }, label: "Solar panel", terminalOverrides: {
      pos: terminal("right", 43 / 110), neg: terminal("right", 67 / 110),
    } },
    "pv-isolator": { position: { x: 290, y: 25 }, rotation: 0 },
    mppt: { position: { x: 455, y: 0 }, label: "Solar controller", terminalOverrides: {
      "pv-pos": terminal("left", 43 / 110), "pv-neg": terminal("left", 67 / 110),
      "batt-pos": terminal("bottom", 0.3), "batt-neg": terminal("right", 0.7),
    } },
    battery: { position: { x: 237.5, y: 225 }, terminalOverrides: {
      pos: terminal("left", 0.5), neg: terminal("right", 0.5),
    } },
    posbus: { position: { x: 155, y: 238 }, rotation: 180, terminalOverrides: {
      b: terminal("right", 0.8), c: terminal("right", 0.5), d: terminal("right", 0.2),
    } },
    negbus: { position: { x: 565, y: 238 }, rotation: 0, terminalOverrides: {
      b: terminal("right", 0.8), c: terminal("right", 0.5), d: terminal("right", 0.2),
    } },
    fridge: { position: { x: 100, y: 415 }, label: "Fridge", terminalOverrides: {
      pos: terminal("top", 0.3), neg: terminal("bottom", 0.7),
    } },
    led: { position: { x: 400, y: 415 }, label: "Cabin lights", terminalOverrides: {
      pos: terminal("top", 0.3), neg: terminal("bottom", 0.7),
    } },
  };
  const system = {
    ...original,
    nodes: original.nodes.map((node) => node.kind === "component" ? { ...node, ...geometry[node.id] } : node),
  };

  return routeExample(system, {
    h1: () => [],
    h2: () => [],
    h11: () => [],
    h12: () => [],
    h3: (from, to) => [{ x: from.x, y: 170 }, { x: 85, y: 170 }, { x: 85, y: to.y }],
    h4: viaX(650),
    h5: () => [],
    h6: () => [],
    h7: (from, to) => [{ x: 70, y: from.y }, { x: 70, y: 395 }, { x: to.x, y: 395 }],
    h9: (from, to) => [{ x: 105, y: from.y }, { x: 105, y: 375 }, { x: to.x, y: 375 }],
    h8: (from, to) => [{ x: from.x, y: 550 }, { x: 635, y: 550 }, { x: 635, y: to.y }],
    h10: (from, to) => [{ x: from.x, y: 530 }, { x: 615, y: 530 }, { x: 615, y: to.y }],
  });
}
